/**
 * API Client for Guild Insights
 *
 * Handles all HTTP requests to the backend API with:
 * - Automatic token injection from Auth0
 * - Type-safe responses
 * - Error handling
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

/**
 * Create an API client with auth token
 */
export function createApiClient(getAccessToken: () => Promise<string>) {
  async function request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {} } = options;

    // Get auth token
    let token: string | null = null;
    try {
      token = await getAccessToken();
    } catch {
      // Token not available (user not logged in)
    }

    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    };

    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
    });

    const data: ApiResponse<T> = await response.json();

    if (!response.ok || !data.success) {
      throw new ApiError(
        data.error?.code || 'UNKNOWN_ERROR',
        data.error?.message || 'An error occurred',
        response.status,
        data.error?.details
      );
    }

    return data.data as T;
  }

  /**
   * Fetch a blob (for file downloads like PDF, CSV)
   */
  async function requestBlob(endpoint: string): Promise<Blob> {
    let token: string | null = null;
    try {
      token = await getAccessToken();
    } catch {
      // Token not available
    }

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new ApiError(
        'DOWNLOAD_FAILED',
        'Failed to download file',
        response.status
      );
    }

    return response.blob();
  }

  return {
    get: <T>(endpoint: string, options?: Omit<ApiOptions, 'method' | 'body'>) =>
      request<T>(endpoint, { ...options, method: 'GET' }),

    post: <T>(endpoint: string, body?: unknown, options?: Omit<ApiOptions, 'method' | 'body'>) =>
      request<T>(endpoint, { ...options, method: 'POST', body }),

    put: <T>(endpoint: string, body?: unknown, options?: Omit<ApiOptions, 'method' | 'body'>) =>
      request<T>(endpoint, { ...options, method: 'PUT', body }),

    patch: <T>(endpoint: string, body?: unknown, options?: Omit<ApiOptions, 'method' | 'body'>) =>
      request<T>(endpoint, { ...options, method: 'PATCH', body }),

    delete: <T>(endpoint: string, options?: Omit<ApiOptions, 'method' | 'body'>) =>
      request<T>(endpoint, { ...options, method: 'DELETE' }),

    getBlob: (endpoint: string) => requestBlob(endpoint),
  };
}

/**
 * Custom API error class
 */
export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Type helper for query functions
 */
export type ApiClient = ReturnType<typeof createApiClient>;
